import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import type { OutreachSourceType } from "features/integrations/types";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, post } from "common/helpers/HTTP";

import type {
  CrmLeadListSource,
  DripLeadListSource,
  EventLeadListSource,
  GroupLeadListSource,
  LeadListSource,
  ListLeadListSource,
  NavigatorLeadListSource,
  PostLeadListSource,
  SearchLeadListSource,
} from "../types";

type CreateSourceParams =
  | {
      settings: (
        | GroupLeadListSource
        | PostLeadListSource
        | EventLeadListSource
        | NavigatorLeadListSource
        | SearchLeadListSource
      )["settings"];
      miner: string;
    }
  | {
      settings: CrmLeadListSource["settings"];
      crm_type: OutreachSourceType;
    }
  | {
      settings: (ListLeadListSource | DripLeadListSource)["settings"];
    };

async function createSourceRequest(
  workspaceId: string,
  listId: string,
  params: CreateSourceParams,
): Promise<LeadListSource> {
  return (
    await post(`workspaces/${workspaceId}/lists/${listId}/source`, {
      ...params,
      type: params.settings.type,
    })
  ).source;
}

async function deleteLeadListSourceRequest(
  workspaceId: string,
  listId: string,
  sourceId: string,
) {
  await del(`workspaces/${workspaceId}/lists/${listId}/source/${sourceId}`);
}

export default function useLeadListSourceMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { listsKeys } = getQueryKeys(workspaceId);

  const { mutateAsync: createSource, isPending: isCreatingSource } =
    useMutation({
      mutationFn: ({
        listId,
        params,
      }: {
        listId: string;
        params: CreateSourceParams;
      }) => createSourceRequest(workspaceId, listId, params),
      onSuccess: (newSource, { listId }) => {
        queryClient.setQueryData<LeadListSource[]>(
          listsKeys.sources(listId),
          (prevSources) =>
            prevSources ? [...prevSources, newSource] : undefined,
        );
      },
    });

  const { mutateAsync: deleteSource, isPending: isDeletingSource } =
    useMutation({
      mutationFn: ({
        listId,
        sourceId,
      }: {
        listId: string;
        sourceId: string;
      }) => deleteLeadListSourceRequest(workspaceId, listId, sourceId),
      onSuccess: (_, { listId, sourceId }) => {
        queryClient.setQueryData<LeadListSource[]>(
          listsKeys.sources(listId),
          (prevSources) =>
            prevSources?.filter((source) => source.id !== sourceId),
        );
        toast.success("Source deleted");
      },
      onError: () => toast.error("Failed to delete source"),
    });

  return {
    createSource,
    isCreatingSource,
    deleteSource,
    isDeletingSource,
  };
}
